import { useRef, useState } from "react"
import CodeInput, { CODE_LENGTH } from "../components/CodeInput"
import JoinCard from "../components/JoinCard"
import { TotalSteps } from "../joinSteps"

interface Props {
  /** Where Resy texted the code, formatted for display. */
  phone: string
  onSubmit: (code: string) => Promise<void>
  /** Resolves to whether a new code actually went out. */
  onResend: () => Promise<boolean>
  onBack: () => void
  error: string
  clearError: () => void
}

export default function CodeStep({ phone, onSubmit, onResend, onBack, error, clearError }: Props) {
  const [code, setCode] = useState("")
  const [sending, setSending] = useState(false)
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)
  const inFlight = useRef(false)

  // Autofill and the submit button can both fire in the same tick
  const submit = async (value: string) => {
    if (inFlight.current) {
      return
    }
    inFlight.current = true
    setSending(true)
    await onSubmit(value)
    inFlight.current = false
    setSending(false)
  }

  const resend = async () => {
    setResending(true)
    setResent(false)
    clearError()
    const sent = await onResend()
    setResending(false)
    setResent(sent)
  }

  return (
    <JoinCard
      title="Enter your code"
      subtitle={`Texted to ${phone}`}
      step={2}
      totalSteps={TotalSteps}
      onBack={onBack}
      backLabel="Use a different number"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (code.length === CODE_LENGTH) {
            submit(code)
          }
        }}
      >
        <CodeInput
          value={code}
          invalid={!!error}
          disabled={sending}
          onChange={(value) => {
            setCode(value)
            clearError()
          }}
          onComplete={submit}
        />
        {error && <div className="invalid-feedback d-block">{error}</div>}

        <button
          className="btn btn-primary btn-lg w-100 mt-3"
          type="submit"
          disabled={code.length !== CODE_LENGTH || sending}
        >
          {sending ? "Verifying..." : "Verify"}
        </button>

        <button
          type="button"
          className="btn btn-link join-resend"
          onClick={resend}
          disabled={resending || sending}
        >
          {resending ? "Resending..." : resent ? "Code resent" : "Resend code"}
        </button>
      </form>
    </JoinCard>
  )
}
